import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { Shield, CheckCircle, AlertCircle, Loader } from 'lucide-react';

export default function SecurityQuestionsSetupPage() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get username from navigation state or location
  const username = location.state?.username || '';
  const email = location.state?.email || '';
  const name = location.state?.name || '';
  
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!username) {
      // Redirect to register if no username
      navigate('/standalone-register');
      return;
    }
    loadQuestions();
  }, [username, navigate]);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/password_reset_inapp.php?action=list_questions');
      const data = await response.json();
      
      if (data.success && data.questions) {
        setAvailableQuestions(data.questions);
      } else {
        setError('Errore nel caricamento delle domande');
      }
    } catch (error) {
      console.error('Error loading questions:', error);
      setError('Errore di connessione. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestion = (question) => {
    const isSelected = selectedQuestions.some(q => q.id === question.id);
    
    if (isSelected) {
      // Remove question
      setSelectedQuestions(selectedQuestions.filter(q => q.id !== question.id));
      const newAnswers = { ...answers };
      delete newAnswers[question.id];
      setAnswers(newAnswers);
    } else {
      // Add question (max 3)
      if (selectedQuestions.length < 3) {
        setSelectedQuestions([...selectedQuestions, question]);
        setAnswers({ ...answers, [question.id]: '' });
      } else {
        setError('Puoi selezionare massimo 3 domande');
      }
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (selectedQuestions.length !== 3) {
      setError('Seleziona esattamente 3 domande');
      return;
    }
    
    for (const question of selectedQuestions) {
      if (!answers[question.id] || answers[question.id].trim() === '') {
        setError('Compila tutte le risposte');
        return;
      }
    }
    
    setSaving(true);
    setError(null);
    
    try {
      // Prepare answers array
      const answersArray = selectedQuestions.map(q => ({
        question_id: q.id,
        answer: answers[q.id].trim(),
      }));
      
      const response = await fetch('/api/password_reset_inapp.php?action=setup_questions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          answers: answersArray,
          role: 'standalone',
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Domande di sicurezza configurate con successo! Ora puoi effettuare il login.' 
            } 
          });
        }, 2000);
      } else {
        setError(data.error || 'Errore nel salvataggio delle domande');
      }
    } catch (error) {
      console.error('Error saving questions:', error);
      setError('Errore di connessione. Riprova.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-indigo-600 dark:text-indigo-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Caricamento domande...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="w-full max-w-md p-6 bg-white dark:bg-gray-800 rounded-xl shadow-md text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2 text-gray-800 dark:text-white">
            Domande configurate!
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Reindirizzamento al login...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-screen p-4 bg-gray-100 dark:bg-gray-900">
      <div className="w-full max-w-2xl p-6 bg-white dark:bg-gray-800 rounded-xl shadow-md">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
            Domande di Sicurezza
          </h2>
        </div>
        
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Seleziona 3 domande di sicurezza e fornisci le risposte. Queste ti aiuteranno a recuperare la password in caso di smarrimento.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Available Questions */}
          <div>
            <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-white">
              Domande Disponibili ({selectedQuestions.length}/3 selezionate)
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {availableQuestions.map((question) => {
                const isSelected = selectedQuestions.some(q => q.id === question.id);
                return (
                  <button
                    key={question.id}
                    type="button"
                    onClick={() => toggleQuestion(question)}
                    className={`w-full p-3 text-left border rounded-lg transition ${
                      isSelected
                        ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-700'
                        : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:border-indigo-300 dark:hover:border-indigo-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${isSelected ? 'font-medium text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-300'}`}>
                        {question.question}
                      </span>
                      {isSelected && (
                        <CheckCircle className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected Questions with Answers */}
          {selectedQuestions.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-white">
                Risposte
              </h3>
              <div className="space-y-4">
                {selectedQuestions.map((question, index) => (
                  <div key={question.id}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {index + 1}. {question.question}
                    </label>
                    <input
                      type="text"
                      value={answers[question.id] || ''}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Inserisci la risposta"
                      required
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/standalone-register')}
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              Indietro
            </button>
            <button
              type="submit"
              disabled={saving || selectedQuestions.length !== 3}
              className="flex-1 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Salvataggio...' : 'Salva Domande'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

